import { PayloadAction, WithSlice, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify/unstyled";
import { rootReducer } from "Store/rootReducer";
import { getToastNotificationId } from "../utils";

export type UnsavedChange = {
  text: string;
  navigateTo: string;
  remindMe: boolean;
  /** Special route for deciding when to show the notification.
   * Used in cases when we want only a single "do not remind me" for all nested routes */
  matchingRoute?: string;
};

type NotificationsStateType = {
  insights: Record<string, UnsavedChange>;
  policy: Record<string, UnsavedChange>;
};

export type NotificationStatePage = keyof NotificationsStateType;

const initialState: NotificationsStateType = { insights: {}, policy: {} };

const notificationsSlice = createSlice({
  name: "notifications",
  initialState: initialState,
  reducers: {
    addUnsavedChange: (
      state,
      action: PayloadAction<{
        id: string;
        page: keyof NotificationsStateType;
        unsavedChange: UnsavedChange;
      }>,
    ) => {
      if (!state[action.payload.page][action.payload.id]) {
        state[action.payload.page][action.payload.id] =
          action.payload.unsavedChange;
      }
    },
    removeUnsavedChange: (
      state,
      action: PayloadAction<{ id: string; page: NotificationStatePage }>,
    ) => {
      const { page, id } = action.payload;

      if (state[page][id]) {
        delete state[page][id];
        toast.dismiss(getToastNotificationId(page, id));
      }
    },
    updateRemindMe: (
      state,
      action: PayloadAction<{
        id: string;
        page: NotificationStatePage;
        remindMe: boolean;
      }>,
    ) => {
      state[action.payload.page][action.payload.id].remindMe =
        action.payload.remindMe;
    },
  },
  selectors: {
    selectUnsavedChangesForPage: (state, page: NotificationStatePage) =>
      state[page],
    selectAllUnsavedChanges: (state) => state,
    selectHasUnsavedChanges: (state) =>
      Object.values(state).some((page) => Object.keys(page).length > 0),
  },
});

const injectedSlice = notificationsSlice.injectInto(rootReducer);

export const { addUnsavedChange, removeUnsavedChange, updateRemindMe } =
  notificationsSlice.actions;

export const {
  selectUnsavedChangesForPage,
  selectAllUnsavedChanges,
  selectHasUnsavedChanges,
} = injectedSlice.selectors;

declare module "Store/rootReducer" {
  export interface LazyLoadedSlices
    extends WithSlice<typeof notificationsSlice> {}
}
